"use client";

// The reading body for a company post.
//
// Shapes, one voice:
//   • an `update` (the daily product dispatch) carries its anatomy in `data` —
//     the statistics highlight, the tick-list sections, the thread, the signoff
//     — rendered natively so the numbers stay big and digestible (the dispatch
//     card treatment, now living inside the blog).
//   • the weekly letter carries `data.letter === 'week'` and renders through
//     LetterBody, so a feature is a row rather than an image of its own name.
//   • a `blog` post is markdown prose: the first paragraph reads as the lede,
//     the rest flows through the house Markdown renderer.

import Markdown from "../Markdown";
import LetterBody from "./LetterBody";
import Embed from "./embeds/Embeds";
import type { CompanyPost, DispatchData, DispatchItem, LetterData, ReleaseData, ReportData } from "../../lib/company/types";
import { renderableCount } from "../../lib/dispatch/normalize";
import { quiet } from "../../lib/company/quiet";
import { dispatchSections } from "../../lib/company/dispatchSections";

type PostData = DispatchData | ReleaseData | LetterData | ReportData;

/** a report names itself — nothing else carries `report`. */
function isReport(d: PostData): d is ReportData {
  return typeof (d as ReportData).report === "string";
}

/** the instant a report's numbers were true, the way a founder says a date —
 *  in india, where the founders keep their calendar (a report read at 02:00
 *  IST on the 7th is the 7th's reading, though the server's clock says the 6th). */
function asOfLine(iso: string, days?: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const when = d.toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  return `read as of ${when.toLowerCase()} ist${typeof days === "number" ? ` · day ${days} since launch` : ""}`;
}

/** a release carries `sections` (the changelog groups); a dispatch does not. */
function isRelease(d: PostData): d is ReleaseData {
  return Array.isArray((d as ReleaseData).sections);
}

/** the weekly letter names itself — nothing else carries `letter`. */
function isLetter(d: PostData): d is LetterData {
  return (d as LetterData).letter === "week";
}

function DispatchBody({ data }: { data: DispatchData }) {
  const stats = data.stats ?? [];
  return (
    <div>
      {stats.length > 0 && (
        <div className="co-stats">
          {stats.map((st, i) => (
            <span key={i} className="co-stat">
              <b>{String(st.value)}</b>
              <span>{quiet(st.label)}</span>
            </span>
          ))}
        </div>
      )}
      {dispatchSections(data).map((sec) => (
        <section key={sec.key} className={sec.aside ? "co-block co-block--aside" : "co-block"}>
          <div className="co-block__label">{quiet(sec.label)}</div>
          {sec.line && <div className="co-block__line">{quiet(sec.line)}</div>}
          {sec.items.map((it, i) => {
            const title = typeof it === "string" ? it : it.title;
            const note = typeof it === "string" ? "" : it.note ?? "";
            return (
              <div key={i} className="co-item">
                <span className="co-item__tick" aria-hidden />
                <span className="co-item__t">
                  {quiet(title)}
                  {note && <span className="co-item__n">{quiet(note)}</span>}
                </span>
              </div>
            );
          })}
        </section>
      ))}
      {data.thread && <div className="co-thread">{quiet(data.thread)}</div>}
      <div className="co-sign">
        <span className="co-sign__seed" aria-hidden />
        <span className="co-sign__name">
          — {quiet(data.signoff ?? "jonna")}
          <span className="co-sign__role">{quiet(data.signrole ?? "product · innernet")}</span>
        </span>
      </div>
      {data.provenance && <div className="co-prov">{quiet(data.provenance)}</div>}
    </div>
  );
}

// a report (kind='report') — the numbers it stands on as a strip, the instant
// they were true, then the reading itself as prose. A report always says WHEN:
// a reading without its date is how a three-month-old diagnosis kept being
// quoted as the present.
function ReportBody({ data, source }: { data: ReportData; source: string }) {
  const stats = data.stats ?? [];
  const asOf = data.as_of ? asOfLine(data.as_of, data.days_since_launch) : "";
  return (
    <div>
      {asOf && <div className="co-prov" style={{ marginTop: 0, marginBottom: "1.1rem" }}>{asOf}</div>}
      {stats.length > 0 && (
        <div className="co-stats">
          {stats.map((st, i) => (
            <span key={i} className="co-stat">
              <b>{String(st.value)}</b>
              <span>{quiet(st.label)}</span>
            </span>
          ))}
        </div>
      )}
      <BlogBody source={source} />
    </div>
  );
}

// a versioned release (kind='update') — the Keep-a-Changelog groups
// (added/changed/fixed…) as tick sections, granular by design.
function ReleaseBody({ data }: { data: ReleaseData }) {
  return (
    <div>
      {data.headline && <p className="co-lede">{quiet(data.headline)}</p>}
      {data.sections.map((sec, si) => (
        <section key={si} className="co-block">
          <div className="co-block__label">{quiet(sec.label)}</div>
          {sec.items.map((it, i) => (
            <div key={i} className="co-item">
              <span className="co-item__tick" aria-hidden />
              <span className="co-item__t">
                {quiet(it.title)}
                {it.note && <span className="co-item__n">{quiet(it.note)}</span>}
                {it.image && (
                  // a real screen of what the line names (the launch letter's pictures)
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className="co-item__pic" src={it.image.src} alt={it.image.alt} loading="lazy" decoding="async" />
                )}
              </span>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function BlogBody({ source }: { source: string }) {
  // drop empty heading markers, then lift the first plain paragraph as the lede
  const cleaned = source
    .split("\n")
    // A picture of a title never renders. Letters published before the weekly
    // letter carried structure have `![the you space](…/feature-card?line=the+you+space)`
    // baked into their markdown — an image whose entire content is the heading
    // beneath it. Those rows are archived and cannot be rewritten by a reader, so
    // the read surface drops them and the sentence carries the feature alone.
    .filter((l) => !/^!\[[^\]]*\]\([^)]*\/api\/brand\/feature-card/.test(l.trim()))
    .filter((l) => !/^#{1,6}\s*$/.test(l.trim()))
    .join("\n")
    .trim();
  const blocks = cleaned.split(/\n\s*\n/);
  const first = blocks[0] ?? "";
  const isPlain = first && !/^[#>\-*`]/.test(first.trimStart());
  const lede = isPlain ? first.replace(/\n/g, " ") : null;
  const rest = (isPlain ? blocks.slice(1) : blocks).join("\n\n");
  // live pieces: a fence written ```embed <name>``` renders the real thing
  // (components/company/embeds) instead of code. Everything between them stays
  // markdown, so a post without embeds renders exactly as it always has.
  const parts = rest.split(/```embed[ \t]+([a-z-]+)[^\n]*\n[\s\S]*?```/);
  return (
    <div>
      {lede && <p className="co-lede">{lede}</p>}
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Embed key={i} name={part} />
        ) : part.trim() ? (
          <Markdown key={i} source={part.trim()} variant="artifact" style={{ fontSize: "1.07rem" }} />
        ) : null,
      )}
    </div>
  );
}

export default function PostBody({ post }: { post: CompanyPost }) {
  // four shapes, one voice: a release renders its changelog groups; the weekly
  // letter renders its sections and quotes; a dispatch renders its stats + tick
  // sections; anything else renders markdown prose.
  // Narrowed inside ONE block, so each guard actually removes its shape from the
  // union — `data && isLetter(data)` as separate statements leaves `data` wide in
  // the else, and the dispatch counter would then be handed a letter.
  const data = post.data;
  if (data) {
    if (isReport(data)) return <div className="co-prose"><ReportBody data={data} source={post.body_md ?? ""} /></div>;
    if (isLetter(data)) return <div className="co-prose"><LetterBody data={data} /></div>;
    if (isRelease(data)) return <div className="co-prose"><ReleaseBody data={data} /></div>;
    // The gate used to be `stats || shipped || thread`, which failed an edition
    // made only of `fixed` / `in_flight` / `notes` — all legal, all named in the
    // dispatch's own contract — and dropped it into BlogBody. With body_md null,
    // that rendered an empty page. Ask the one function that counts everything.
    if (renderableCount(data) > 0) return <div className="co-prose"><DispatchBody data={data} /></div>;
  }
  return <div className="co-prose"><BlogBody source={post.body_md ?? ""} /></div>;
}
